import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "p-4 pt-0 surface-100" }
const _hoisted_2 = { class: "field" }
const _hoisted_3 = { class: "p-error" }
const _hoisted_4 = { class: "formgrid flex" }
const _hoisted_5 = { class: "field col p-0 pr-2 flex flex-column" }
const _hoisted_6 = { class: "p-error" }
const _hoisted_7 = { class: "field col p-0 pl-2 flex flex-column" }
const _hoisted_8 = { class: "p-error" }
const _hoisted_9 = { class: "field" }
const _hoisted_10 = { class: "p-error" }
const _hoisted_11 = {
  key: 0,
  class: "formgrid flex"
}
const _hoisted_12 = { class: "field col p-0 pr-2" }
const _hoisted_13 = { class: "p-error" }
const _hoisted_14 = { class: "field col col p-0 pl-2" }
const _hoisted_15 = { class: "p-error" }
const _hoisted_16 = { class: "field flex flex-column" }
const _hoisted_17 = { class: "p-error" }
const _hoisted_18 = { class: "field flex flex-column" }
const _hoisted_19 = { class: "field" }
const _hoisted_20 = { class: "p-error" }
const _hoisted_21 = { class: "field" }
const _hoisted_22 = { class: "p-error" }

import { computed, onBeforeMount, reactive, ref, Ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers, minValue, required, requiredIf } from '@vuelidate/validators';
import { PersonStores, useAuthStore, useDisciplineStore, useServicesStore } from '@/store';
import Dialog from 'primevue/dialog';
import Calendar from 'primevue/calendar';
import InputSwitch from 'primevue/inputswitch';
import InputNumber from 'primevue/inputnumber';
import { TreatmentPlanServiceUpdateFormInterface, TreatmentPlanServiceUpdateInterface } from '@/types/TreatmentPlans';
import { TreatmentPlanTypeGenericInterface, UserTreatmentPlansStateInterface } from '@/types';
import { FREQUENCY_INTERVALS_OPTIONS, FREQUENCY_AMOUNT, SHORTCUT_FREQUENCY_TYPES, FREQUENCY_INTERVALS } from '@/enum';
import { ServiceInterface } from '@/types/Service';
import { usePersonTypes } from '@/composables';

interface PropsInterface {
  service: TreatmentPlanServiceUpdateInterface | undefined;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UpdateService',
  props: {
    service: {}
  },
  emits: ["update", "cancel"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

const updateService = reactive<TreatmentPlanServiceUpdateFormInterface>({
  id: props.service.id,
  treatment_service_type_id: props.service.treatment_service_type.id,
  discipline_id: props.service.discipline.id,
  start_at: new Date(props.service.start_at),
  end_at: new Date(props.service.end_at),
  duration: props.service.duration,
  treatment_frequency_type: {},
  frequency: props.service.frequency,
  interval: props.service?.interval,
  service_id: props.service.service.id,
  is_group: props.service.is_group,
});

const authStore = useAuthStore();
const disciplineStore = useDisciplineStore();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const { connections } = PersonStores.useUserConnectionsStore();
const { person } = PersonStores.useUserProfileStore();
const servicesStore = useServicesStore();
const disciplines: Ref<{ id: number; name: string }[]> = ref([]);
const frequencyTypes: Ref<UserTreatmentPlansStateInterface[]> = ref([]);
const serviceTypes: Ref<TreatmentPlanTypeGenericInterface[]> = ref([]);
const services: Ref<ServiceInterface[]> = ref([]);
const modalVisible = ref(true);
const customFrequencySelected = computed(() => updateService.treatment_frequency_type?.acronym === 'CUSTOM');
const organizationId = computed(() => {
  const isStudent = usePersonTypes.isStudent(person?.people_type?.acronym as string);
  if (isStudent && connections?.organizations && connections.organizations.length > 0) {
    return connections.organizations[0].id;
  }

  return authStore.organizationId;
});
const frequencyIntervals = FREQUENCY_INTERVALS_OPTIONS;
const frequencyAmount = FREQUENCY_AMOUNT;

const frequencyTypeRequired = (value: TreatmentPlanServiceUpdateInterface['treatment_frequency_type']) => !!value?.id;

const findInFrequencyType = (acronym: string) => {
  const type = treatmentPlansStore.treatmentPlanFrequencyTypes.find((type) => type.acronym === acronym);
  return type ? { id: type.id, acronym: type.acronym } : {};
};

const rules = computed(() => {
  return {
    treatment_service_type_id: {
      required: helpers.withMessage('Please select Service Type', minValue(1)),
    },
    discipline_id: {
      minValue: helpers.withMessage('Please select Discipline', minValue(1)),
    },
    start_at: {
      required: helpers.withMessage('Please choose Start Date', required),
    },
    end_at: {
      required: helpers.withMessage('Please choose End Date', required),
      minValue: helpers.withMessage(
        'Please choose a date greater than or equal to Start Date',
        minValue(updateService.start_at as string),
      ),
    },
    treatment_frequency_type: {
      required: helpers.withMessage('Please select Frequency', frequencyTypeRequired),
    },
    duration: { required: helpers.withMessage('Please choose Duration', required) },
    frequency: {
      requiredIfCustom: helpers.withMessage('Please select Frequency', requiredIf(customFrequencySelected.value)),
    },
    interval: {
      requiredIfCustom: helpers.withMessage('Please select Interval', requiredIf(customFrequencySelected.value)),
    },
    service_id: {
      required: helpers.withMessage('Please select Service', minValue(1)),
    },
  };
});

const v$ = useVuelidate(rules, updateService, { $autoDirty: true });

onBeforeMount(async () => {
  await fetchDataForForm();
  setTreatmentFrequencyType();
});

const fetchDataForForm = async () => {
  const disciplineData = await disciplineStore.getDisciplinesOfOrganizationForDropdown(organizationId.value as number);
  const frequencyData = await treatmentPlansStore.getTreatmentPlanFrequencyTypes();
  const serviceTypesData = await treatmentPlansStore.getTreatmentServiceTypes();
  await servicesStore.getOrganizationServices(organizationId.value as number);
  disciplines.value = disciplineData as [];
  frequencyTypes.value = frequencyData as [];
  serviceTypes.value = serviceTypesData;
  services.value = servicesStore.availableServices as [];
};

/**
 * we need to take the format and interval and if they are like
 * the shortcuts then assign them back to the shortcut
 * and all other interval or frequencies we assign to their type
 * @param frequency
 * @param interval
 */
const formatFrequencyType = (frequency: number, interval: string) => {
  if (frequency === 2 && interval === FREQUENCY_INTERVALS.WEEKLY)
    return findInFrequencyType(SHORTCUT_FREQUENCY_TYPES.WEEK2);
  if (frequency === 3 && interval === FREQUENCY_INTERVALS.WEEKLY)
    return findInFrequencyType(SHORTCUT_FREQUENCY_TYPES.WEEK3);
  if (frequency === 2 && interval === FREQUENCY_INTERVALS.MONTHLY)
    return findInFrequencyType(SHORTCUT_FREQUENCY_TYPES.MONTH2);

  return findInFrequencyType(interval.toUpperCase());
};

const setTreatmentFrequencyType = () =>
  (updateService.treatment_frequency_type = formatFrequencyType(props.service.frequency, props.service.interval));

const save = () => {
  emit('update', updateService);
  return;
};

const cancel = () => {
  emit('cancel');
  modalVisible.value = false;
  return;
};

return (_ctx: any,_cache: any) => {
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_AppButton = _resolveComponent("AppButton")!

  return (_openBlock(), _createBlock(_unref(Dialog), {
    visible: modalVisible.value,
    "onUpdate:visible": _cache[10] || (_cache[10] = ($event: any) => ((modalVisible).value = $event)),
    draggable: false,
    class: "dialog__container new-event-modal",
    modal: true,
    onHide: cancel
  }, {
    header: _withCtx(() => [...(_cache[11] || (_cache[11] = [
      _createElementVNode("h2", { textContent: 'Update Service' }, null, -1)
    ]))]),
    default: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[12] || (_cache[12] = _createElementVNode("label", { for: "discipline" }, "Discipline", -1)),
          _createVNode(_component_Dropdown, {
            id: "discipline",
            modelValue: updateService.discipline_id,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((updateService.discipline_id) = $event)),
            class: _normalizeClass(["w-full", { 'p-invalid': _unref(v$).discipline_id.$invalid && _unref(v$).discipline_id.$dirty }]),
            options: disciplines.value,
            "option-value": "id",
            "option-label": "name",
            placeholder: "Select Discipline",
            onBlur: _unref(v$).discipline_id.$touch
          }, null, 8, ["modelValue", "options", "class", "onBlur"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).discipline_id.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_3, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _cache[13] || (_cache[13] = _createElementVNode("label", { for: "start_at" }, "Start Date", -1)),
            _createVNode(_unref(Calendar), {
              id: "start_at",
              modelValue: updateService.start_at,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((updateService.start_at) = $event)),
              placeholder: "mm/dd/yyyy",
              class: _normalizeClass({ 'p-invalid': _unref(v$).start_at.$invalid && _unref(v$).start_at.$dirty, 'w-full': true, 'pr-2': true }),
              onBlur: _unref(v$).start_at.$touch
            }, null, 8, ["modelValue", "class", "onBlur"]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).start_at.$errors, (error) => {
              return (_openBlock(), _createElementBlock("div", {
                key: error.$uid,
                class: "input-errors"
              }, [
                _createElementVNode("div", _hoisted_6, _toDisplayString(error.$message), 1)
              ]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_7, [
            _cache[14] || (_cache[14] = _createElementVNode("label", { for: "end_at" }, "End Date", -1)),
            _createVNode(_unref(Calendar), {
              id: "end_at",
              modelValue: updateService.end_at,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((updateService.end_at) = $event)),
              placeholder: "mm/dd/yyyy",
              class: _normalizeClass({ 'p-invalid': _unref(v$).end_at.$invalid && _unref(v$).end_at.$dirty, 'w-full': true }),
              onBlur: _unref(v$).end_at.$touch
            }, null, 8, ["modelValue", "class", "onBlur"]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).end_at.$errors, (error) => {
              return (_openBlock(), _createElementBlock("div", {
                key: error.$uid,
                class: "input-errors"
              }, [
                _createElementVNode("div", _hoisted_8, _toDisplayString(error.$message), 1)
              ]))
            }), 128))
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _cache[15] || (_cache[15] = _createElementVNode("label", { for: "frequency" }, "Frequency", -1)),
          _createVNode(_component_Dropdown, {
            id: "frequency",
            modelValue: updateService.treatment_frequency_type,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((updateService.treatment_frequency_type) = $event)),
            class: _normalizeClass(["w-full", { 'p-invalid': _unref(v$).treatment_frequency_type.$invalid && _unref(v$).treatment_frequency_type.$dirty }]),
            options: frequencyTypes.value,
            "option-value": 
              (opt) => {
                return { id: opt.id, acronym: opt.acronym };
              }
            ,
            "option-label": "title",
            placeholder: "Select Frequency",
            onBlur: _unref(v$).treatment_frequency_type.$touch
          }, null, 8, ["modelValue", "options", "option-value", "class", "onBlur"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).treatment_frequency_type.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_10, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ]),
        (customFrequencySelected.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                _cache[16] || (_cache[16] = _createElementVNode("label", { for: "interval" }, "Interval", -1)),
                _createVNode(_component_Dropdown, {
                  id: "interval",
                  modelValue: updateService.interval,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((updateService.interval) = $event)),
                  options: _unref(frequencyIntervals),
                  "option-value": "value",
                  "option-label": "label",
                  placeholder: "Select Interval",
                  class: _normalizeClass({ 'p-invalid': _unref(v$).interval.$invalid && _unref(v$).interval.$dirty, 'w-full': true }),
                  onBlur: _unref(v$).interval.$touch
                }, null, 8, ["modelValue", "options", "class", "onBlur"]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).interval.$errors, (error) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: error.$uid,
                    class: "input-errors"
                  }, [
                    _createElementVNode("div", _hoisted_13, _toDisplayString(error.$message), 1)
                  ]))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_14, [
                _cache[17] || (_cache[17] = _createElementVNode("label", { for: "frequency_amount" }, "Frequency", -1)),
                _createVNode(_component_Dropdown, {
                  id: "frequency_amount",
                  modelValue: updateService.frequency,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((updateService.frequency) = $event)),
                  options: _unref(frequencyAmount),
                  "option-value": "value",
                  "option-label": "label",
                  placeholder: "Select Frequency",
                  class: _normalizeClass({ 'p-invalid': _unref(v$).frequency.$invalid && _unref(v$).frequency.$dirty, 'w-full': true }),
                  onBlur: _unref(v$).frequency.$touch
                }, null, 8, ["modelValue", "options", "class", "onBlur"]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).frequency.$errors, (error) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: error.$uid,
                    class: "input-errors"
                  }, [
                    _createElementVNode("div", _hoisted_15, _toDisplayString(error.$message), 1)
                  ]))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_16, [
          _cache[18] || (_cache[18] = _createElementVNode("label", { for: "duration" }, "Duration (Minutes)", -1)),
          _createVNode(_unref(InputNumber), {
            id: "duration",
            modelValue: updateService.duration,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((updateService.duration) = $event)),
            min: 0,
            class: _normalizeClass({ 'p-invalid': _unref(v$).duration.$invalid && _unref(v$).duration.$dirty }),
            onBlur: _unref(v$).duration.$touch
          }, null, 8, ["modelValue", "class", "onBlur"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).duration.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_17, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_18, [
          _cache[19] || (_cache[19] = _createElementVNode("label", { for: "can_group" }, "Can Group", -1)),
          _createVNode(_unref(InputSwitch), {
            id: "can_group",
            modelValue: updateService.is_group,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((updateService.is_group) = $event))
          }, null, 8, ["modelValue"])
        ]),
        _createElementVNode("div", _hoisted_19, [
          _cache[20] || (_cache[20] = _createElementVNode("label", { for: "service" }, "Service", -1)),
          _createVNode(_component_Dropdown, {
            id: "service",
            modelValue: updateService.service_id,
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((updateService.service_id) = $event)),
            class: _normalizeClass(["w-full", { 'p-invalid': _unref(v$).service_id.$invalid && _unref(v$).service_id.$dirty }]),
            options: services.value,
            "option-value": "id",
            "option-label": "name",
            placeholder: "Select Service",
            onBlur: _unref(v$).service_id.$touch
          }, null, 8, ["modelValue", "options", "class", "onBlur"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).service_id.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_20, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_21, [
          _cache[21] || (_cache[21] = _createElementVNode("label", { for: "service_type" }, "Service Type", -1)),
          _createVNode(_component_Dropdown, {
            id: "service_type",
            modelValue: updateService.treatment_service_type_id,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((updateService.treatment_service_type_id) = $event)),
            class: _normalizeClass(["w-full", { 'p-invalid': _unref(v$).treatment_service_type_id.$invalid && _unref(v$).treatment_service_type_id.$dirty }]),
            options: serviceTypes.value,
            "option-value": "id",
            "option-label": "title",
            placeholder: "Select Service Type",
            onBlur: _unref(v$).treatment_service_type_id.$touch
          }, null, 8, ["modelValue", "options", "class", "onBlur"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).treatment_service_type_id.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_22, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ])
      ])
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_AppButton, {
        id: "cancel_service_btn",
        type: "secondary",
        text: "Cancel",
        onClick: cancel
      }),
      _createVNode(_component_AppButton, {
        id: "save_service_btn",
        type: "primary",
        text: "Update",
        disabled: _unref(v$).$invalid,
        onClick: save
      }, null, 8, ["disabled"])
    ]),
    _: 1
  }, 8, ["visible"]))
}
}

})